import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth';
import { api } from '../services/api';
import {
  User,
  Mail,
  Calendar,
  Bell,
  Moon,
  Sun,
  Link2,
  Shield,
  Trash2,
  LogOut,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Unlink,
} from 'lucide-react';
import clsx from 'clsx';
import { toast } from 'sonner';

export function Settings() {
  const { user, logout, updateUser, changePassword } = useAuthStore();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'profile' | 'integrations' | 'preferences' | 'account'>('profile');
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [displayName, setDisplayName] = useState(user?.name || '');
  const [isSaving, setIsSaving] = useState(false);

  // Password change state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.name) {
      setDisplayName(user.name);
    }
  }, [user?.name]);

  // Query Google connection status
  const { data: googleStatus, isLoading: isLoadingGoogleStatus } = useQuery({
    queryKey: ['googleConnectionStatus'],
    queryFn: () => api.getGoogleConnectionStatus(),
    staleTime: 30000, // Cache for 30 seconds
  });

  const handleConnectGoogle = async () => {
    setIsConnecting(true);
    try {
      const { authUrl } = await api.initiateGoogleAuth();
      // Redirect to Google OAuth consent screen
      window.location.href = authUrl;
    } catch (error) {
      console.error('Failed to initiate Google auth:', error);
      toast.error('Failed to connect to Google. Please try again.');
      setIsConnecting(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    if (!confirm('Are you sure you want to disconnect your Google account?')) {
      return;
    }

    setIsDisconnecting(true);
    try {
      await api.disconnectGoogle();
      queryClient.invalidateQueries({ queryKey: ['googleConnectionStatus'] });
    } catch (error) {
      console.error('Failed to disconnect Google:', error);
      toast.error('Failed to disconnect Google account. Please try again.');
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!displayName.trim()) return;

    setIsSaving(true);
    try {
      const response = await api.updateProfile(displayName);
      if (response.success) {
        updateUser({ name: response.data.name });
        toast.success('Profile updated successfully!');
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }

    setIsChangingPassword(true);
    try {
      await changePassword(oldPassword, newPassword);
      toast.success('Password changed successfully!');
      setShowChangePassword(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Failed to change password:', error);
      setPasswordError(error instanceof Error ? error.message : 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'integrations', label: 'Integrations', icon: Link2 },
    { id: 'preferences', label: 'Preferences', icon: Bell },
    { id: 'account', label: 'Account', icon: Shield },
  ] as const;

  return (
    <div className="h-full p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold">Settings</h2>
        <p className="text-sm text-text-tertiary">Manage your account and preferences</p>
      </div>

      <div className="flex gap-6">
        {/* Sidebar */}
        <div className="w-48 space-y-1">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={clsx(
                'flex w-full items-center gap-3 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                activeTab === id
                  ? 'bg-accent-muted text-accent'
                  : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 rounded-xl border border-border bg-surface p-6">
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div>
                <h3 className="mb-4 font-semibold">Profile Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-text-secondary">Email</label>
                    <div className="mt-1 flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2">
                      <Mail className="h-4 w-4 text-text-tertiary" />
                      <span className="text-sm">{user?.email || 'Not set'}</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-text-secondary">Display Name</label>
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="input mt-1"
                      placeholder="Your name"
                    />
                  </div>
                </div>
              </div>
              <div className="border-t border-border pt-6">
                <button
                  className="btn-primary"
                  onClick={handleSaveProfile}
                  disabled={isSaving || !displayName.trim() || displayName === user?.name}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'integrations' && (
            <div className="space-y-6">
              <div>
                <h3 className="mb-4 font-semibold">Connected Services</h3>
                <div className="space-y-4">
                  {/* Google Calendar */}
                  <div className="flex items-center justify-between rounded-lg border border-border bg-background p-4">
                    <div className="flex items-center gap-4">
                      <div className={clsx(
                        'flex h-10 w-10 items-center justify-center rounded-lg',
                        googleStatus?.connected ? 'bg-success-muted' : 'bg-surface-hover'
                      )}>
                        <Calendar className={clsx(
                          'h-5 w-5',
                          googleStatus?.connected ? 'text-success' : 'text-text-secondary'
                        )} />
                      </div>
                      <div>
                        <p className="font-medium">Google Calendar</p>
                        <p className="text-sm text-text-tertiary">
                          {isLoadingGoogleStatus
                            ? 'Checking connection...'
                            : googleStatus?.connected
                              ? 'Connected - Sync your calendar events'
                              : 'Connect to sync your calendar events'}
                        </p>
                      </div>
                    </div>
                    {isLoadingGoogleStatus ? (
                      <Loader2 className="h-5 w-5 animate-spin text-text-tertiary" />
                    ) : googleStatus?.connected ? (
                      <button
                        onClick={handleDisconnectGoogle}
                        disabled={isDisconnecting}
                        className="btn-secondary text-error hover:bg-error-muted"
                      >
                        {isDisconnecting ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Unlink className="h-4 w-4" />
                        )}
                        Disconnect
                      </button>
                    ) : (
                      <button
                        onClick={handleConnectGoogle}
                        disabled={isConnecting}
                        className="btn-primary"
                      >
                        {isConnecting ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <ExternalLink className="h-4 w-4" />
                        )}
                        Connect
                      </button>
                    )}
                  </div>

                  {/* Google Tasks - Note: Uses same OAuth connection */}
                  <div className="flex items-center justify-between rounded-lg border border-border bg-background p-4">
                    <div className="flex items-center gap-4">
                      <div className={clsx(
                        'flex h-10 w-10 items-center justify-center rounded-lg',
                        googleStatus?.connected ? 'bg-success-muted' : 'bg-surface-hover'
                      )}>
                        <CheckCircle2 className={clsx(
                          'h-5 w-5',
                          googleStatus?.connected ? 'text-success' : 'text-text-secondary'
                        )} />
                      </div>
                      <div>
                        <p className="font-medium">Google Tasks</p>
                        <p className="text-sm text-text-tertiary">
                          {googleStatus?.connected
                            ? 'Connected via Google Calendar'
                            : 'Connect Google Calendar to enable tasks'}
                        </p>
                      </div>
                    </div>
                    <span className={clsx(
                      'rounded-full px-3 py-1 text-xs font-medium',
                      googleStatus?.connected
                        ? 'bg-success-muted text-success'
                        : 'bg-surface-hover text-text-tertiary'
                    )}>
                      {googleStatus?.connected ? 'Connected' : 'Not connected'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'preferences' && (
            <div className="space-y-6">
              <div>
                <h3 className="mb-4 font-semibold">Appearance</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Theme</p>
                      <p className="text-sm text-text-tertiary">Choose your preferred theme</p>
                    </div>
                    <div className="flex rounded-lg border border-border p-1">
                      <button className="rounded-md bg-surface-hover px-3 py-1.5">
                        <Moon className="h-4 w-4" />
                      </button>
                      <button className="rounded-md px-3 py-1.5 text-text-tertiary">
                        <Sun className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-border pt-6">
                <h3 className="mb-4 font-semibold">Notifications</h3>
                <div className="space-y-4">
                  <label className="flex cursor-pointer items-center justify-between">
                    <div>
                      <p className="font-medium">Task Reminders</p>
                      <p className="text-sm text-text-tertiary">Get notified about due tasks</p>
                    </div>
                    <input type="checkbox" className="h-5 w-5 accent-accent" defaultChecked />
                  </label>
                  <label className="flex cursor-pointer items-center justify-between">
                    <div>
                      <p className="font-medium">Calendar Alerts</p>
                      <p className="text-sm text-text-tertiary">Event reminders before meetings</p>
                    </div>
                    <input type="checkbox" className="h-5 w-5 accent-accent" defaultChecked />
                  </label>
                  <label className="flex cursor-pointer items-center justify-between">
                    <div>
                      <p className="font-medium">Daily Summary</p>
                      <p className="text-sm text-text-tertiary">Daily overview of your schedule</p>
                    </div>
                    <input type="checkbox" className="h-5 w-5 accent-accent" />
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'account' && (
            <div className="space-y-6">
              <div>
                <h3 className="mb-4 font-semibold">Security</h3>
                {!showChangePassword ? (
                  <div className="space-y-4">
                    <button
                      onClick={() => setShowChangePassword(true)}
                      className="btn-secondary w-full justify-start"
                    >
                      <Shield className="h-4 w-4" />
                      Change Password
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleChangePassword} className="space-y-4 rounded-lg border border-border bg-background p-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium">Change Password</h4>
                      <button
                        type="button"
                        onClick={() => {
                          setShowChangePassword(false);
                          setPasswordError(null);
                        }}
                        className="text-xs text-text-tertiary hover:text-text-secondary"
                      >
                        Cancel
                      </button>
                    </div>

                    {passwordError && (
                      <div className="text-xs text-error bg-error-muted p-2 rounded">
                        {passwordError}
                      </div>
                    )}

                    <div>
                      <label className="text-xs text-text-secondary">Current Password</label>
                      <input
                        type="password"
                        required
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        className="input mt-1"
                      />
                    </div>

                    <div>
                      <label className="text-xs text-text-secondary">New Password</label>
                      <input
                        type="password"
                        required
                        minLength={8}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="input mt-1"
                      />
                    </div>

                    <div>
                      <label className="text-xs text-text-secondary">Confirm New Password</label>
                      <input
                        type="password"
                        required
                        minLength={8}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="input mt-1"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isChangingPassword}
                      className="btn-primary w-full justify-center"
                    >
                      {isChangingPassword ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        'Update Password'
                      )}
                    </button>
                  </form>
                )}
              </div>

              <div className="border-t border-border pt-6">
                <h3 className="mb-4 font-semibold">Session</h3>
                <button
                  onClick={logout}
                  className="btn-secondary w-full justify-start text-warning hover:bg-warning-muted"
                >
                  <LogOut className="h-4 w-4" />
                  Sign Out
                </button>
              </div>

              <div className="border-t border-border pt-6">
                <h3 className="mb-4 font-semibold text-error">Danger Zone</h3>
                <button className="btn-secondary w-full justify-start text-error hover:bg-error-muted">
                  <Trash2 className="h-4 w-4" />
                  Delete Account
                </button>
                <p className="mt-2 text-xs text-text-tertiary">
                  This action is permanent and cannot be undone.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

